import { useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { useStore } from '@tanstack/react-store'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  Check,
  Code,
  Copy,
  Download,
  LayoutTemplate,
  Languages,
  Layers,
  Loader2,
  Plus,
  X,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardIcon, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { AuthService } from '@/services/core/auth'
import { apiErrorDescription, ApiError } from '@/services/core/apiError'
import { TeamsApi } from '@/services/users/teamsApi'
import {
  ReportTemplatesApi,
  TRANSLATION_PREFIX,
  prepareComponents,
  toComponentInput,
  type ReportTemplateDetail,
  type ReportTemplateInput,
} from '@/services/reports/reportTemplatesApi'
import { ReportTranslationsApi } from '@/services/reports/reportTranslationsApi'
import { reportTypesFor, type ReportFormat, type ReportType } from '@/services/reports/reportTypes'
import { formatDate } from '@/utils/format'
import { AdvancedSection } from '../../components/AdvancedSection'
import { AppShell } from '../../components/AppShell'
import { CodeEditor } from '../../components/CodeEditor'
import { FieldLabel } from '../../components/FieldLabel'
import { FormSkeleton } from '../../components/FormSkeleton'
import { InlineEditTitle } from '../../components/InlineEditTitle'
import { NetworkObjectLookup } from '../../components/NetworkObjectLookup'
import { ObjectTypeLabel } from '../../components/ObjectTypeLabel'
import { SearchSelect } from '../../components/SearchSelect'
import { StatsBar, StatsBarActivity, StatsBarId } from '../../components/StatsBar'
import { SwitchField } from '../../components/SwitchField'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog'
import { CopyReportItemDialog } from '../../components/reports/CopyReportItemDialog'
import {
  ReportComponentsEditor,
  type EditableReportComponent,
} from '../../components/reports/ReportComponentsEditor'
import { useNetwork } from '../../contexts/NetworkContext'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'
import {
  ObjectStateToggle,
  useSaveObjectState,
  type ObjectState,
} from '../../components/ObjectStateToggle'
import { VersionHistoryButton } from '@/components/VersionHistoryButton'
import { ValidationWarnings } from '@/components/ValidationWarnings'

const LIST_PATH = '/reports/templates'

const POLITICAL_FILTERS = ['ALL', 'POLITICAL_ONLY', 'EXCLUDE_POLITICAL'] as const
type PoliticalFilter = (typeof POLITICAL_FILTERS)[number]

/** Template parameters the form owns; anything else stored on the template is passed through. */
const OWN_PARAMETERS = new Set(['political', 'limit'])

/** Only the campaign reports print the template's own column labels. */
const hasOwnLabels = (type: string) => type === 'LINE_ITEM' || type === 'SELF_SERVICE'

function labelsOf(parameters: Record<string, unknown>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(parameters).filter(
      (entry): entry is [string, string] =>
        entry[0].startsWith(TRANSLATION_PREFIX) && typeof entry[1] === 'string'
    )
  )
}

// ── Inner form component ────────────────────────────────────────────────────

interface ReportTemplateEditFormProps {
  template: ReportTemplateDetail | null
  id: string
  isNew: boolean
}

function ReportTemplateEditForm({ template, id, isNew }: ReportTemplateEditFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { currentNetwork } = useNetwork()
  const [copyOpen, setCopyOpen] = useState(false)
  const [htmlOpen, setHtmlOpen] = useState(!!(template?.header || template?.footer))
  const [labelsOpen, setLabelsOpen] = useState(false)

  const reportTypes = reportTypesFor(currentNetwork)
  // Political networks can narrow any report on ads to political ads, or leave them out.
  const politicalNetwork = !!currentNetwork?.marketplacePlatform || !!currentNetwork?.politicalApp
  // Teams own templates on marketplace and self-service networks; elsewhere one is only kept.
  const showTeam =
    !!currentNetwork?.marketplacePlatform || !!currentNetwork?.suiPlatform || !!template?.team

  const form = useForm({
    defaultValues: {
      name: template?.name ?? '',
      description: template?.description ?? '',
      reportType: (template?.reportType ?? '') as ReportType | '',
      reportFormat: (template?.reportFormat ?? 'HTML') as ReportFormat,
      teamId: template?.team?.id ?? '',
      political: ((template?.parameters.political as string) || 'ALL') as PoliticalFilter,
      customLogo: !!template?.logo,
      logo: template?.logo ?? '',
      header: template?.header ?? '',
      footer: template?.footer ?? '',
      reportTranslationId: template?.reportTranslation?.id ?? '',
      labels: labelsOf(template?.parameters ?? {}),
      components: (template?.components ?? []).map(
        (c, index): EditableReportComponent => ({ key: `saved-${index}`, ...c })
      ),
    },
    onSubmit: ({ value }) => {
      const hasTranslation = value.reportFormat === 'HTML' && !!value.reportTranslationId
      // Labels stored on the template are kept, unless a report translation now does the labelling.
      const labels = hasTranslation
        ? {}
        : Object.fromEntries(Object.entries(value.labels).filter(([, text]) => text.trim()))
      if (value.reportType === 'SELF_SERVICE' && !hasTranslation && !labels.translationRendered) {
        // Self-service advertisers only ever see rendered impressions, so they read as impressions.
        labels.translationRendered = 'Impressions'
      }
      const passedThrough = Object.fromEntries(
        Object.entries(template?.parameters ?? {}).filter(
          ([key]) => !OWN_PARAMETERS.has(key) && !key.startsWith(TRANSLATION_PREFIX)
        )
      )
      const parameters: Record<string, unknown> = {
        ...passedThrough,
        ...labels,
        ...(politicalNetwork && value.reportType !== 'NETWORK' ? { political: value.political } : {}),
      }
      mutation.mutate({
        name: value.name,
        description: value.description,
        reportType: value.reportType || undefined,
        reportFormat: value.reportFormat,
        logo: value.reportFormat === 'HTML' && value.customLogo ? value.logo.trim() : '',
        header: value.header,
        footer: value.footer,
        parameters,
        components: toComponentInput(prepareComponents(value.components, labels, hasTranslation)),
        reportTranslation: hasTranslation ? { id: value.reportTranslationId } : null,
        ...(value.teamId || template?.team ? { team: value.teamId ? { id: value.teamId } : null } : {}),
        objectState: (template?.objectState ?? 'ACTIVE') as ObjectState,
      })
    },
  })
  const saveObjectState = useSaveObjectState(
    (objectState) => ReportTemplatesApi.update(id, { name: template?.name ?? '', objectState }),
    ['report-template', id],
    ['report-templates']
  )
  const { isDirty, markClean, blocker } = useUnsavedChanges(form)
  const values = useStore(form.store, (s) => s.values)
  const set = form.setFieldValue

  const { data: teams = [], isLoading: teamsLoading } = useQuery({
    queryKey: ['teams', ''],
    queryFn: () => TeamsApi.getAll(),
    enabled: showTeam,
    staleTime: 5 * 60 * 1000,
  })
  const { data: translations = [], isLoading: translationsLoading } = useQuery({
    queryKey: ['report-translations', 'all'],
    queryFn: () => ReportTranslationsApi.getAll(),
    staleTime: 60 * 1000,
  })
  const { data: labelDefaults = {} } = useQuery({
    queryKey: ['report-translation-defaults'],
    queryFn: () => ReportTranslationsApi.getDefaults(),
    staleTime: Infinity,
  })

  const isHtml = values.reportFormat === 'HTML'
  const usesReportTranslation = isHtml && !!values.reportTranslationId
  const showLabels = !usesReportTranslation && hasOwnLabels(values.reportType)
  const englishLabels = labelDefaults.en ?? {}
  const languages = Object.keys(labelDefaults)

  const changeType = (reportType: ReportType) => {
    if (reportType === values.reportType) return
    set('reportType', reportType)
    // Components belong to one report type; another type starts from an empty report.
    set('components', [])
  }

  const loadLabels = (language: string) => set('labels', { ...(labelDefaults[language] ?? {}) })

  const mutation = useMutation({
    mutationFn: (input: ReportTemplateInput) =>
      isNew ? ReportTemplatesApi.create(id, input) : ReportTemplatesApi.update(id, input),
    onSuccess: (data) => {
      queryClient.setQueryData(['report-template', id], data)
      queryClient.invalidateQueries({ queryKey: ['report-templates'] })
      markClean()
      if (isNew) navigate(`${LIST_PATH}/${id}`, { replace: true })
      toast.success(t('common.saved'), {
        description: isNew ? t('reportTemplates.createdSuccess') : t('reportTemplates.savedSuccess'),
        duration: 3000,
      })
    },
    onError: (error) => {
      const summary =
        error instanceof ApiError
          ? error.message
          : isNew
            ? t('reportTemplates.createFailed')
            : t('reportTemplates.saveFailed')
      toast.error(summary, { description: apiErrorDescription(error), duration: 5000 })
    },
  })

  const crumbs = [
    { label: t('reportTemplates.title'), href: LIST_PATH },
    {
      label: isNew
        ? t('reportTemplates.newTemplate')
        : (template?.name ?? t('reportTemplates.editTitle')),
    },
  ]

  return (
    <AppShell
      crumbs={crumbs}
      showBack
      objectType={isNew ? undefined : t('objectTypes.reportTemplate')}
    >
      <form
        className="ad-page"
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit()
        }}
      >
        <ValidationWarnings warnings={template?.validationWarnings} />
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <ObjectTypeLabel type={t('objectTypes.reportTemplate')} />
            <h1>
              <form.Field
                name="name"
                validators={{
                  onChange: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                  onSubmit: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                }}
              >
                {(field) => (
                  <InlineEditTitle
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v)}
                    placeholder={t('reportTemplates.namePlaceholder')}
                    required
                    error={field.state.meta.errors[0] as string | undefined}
                  />
                )}
              </form.Field>
            </h1>
          </div>
          {!isNew && (
            <div className="ad-page__actions">
              <VersionHistoryButton />
              <Button type="button" variant="outline" onClick={() => setCopyOpen(true)}>
                <Copy className="size-3.5" />
                {t('reportsModule.copy')}
              </Button>
              <ObjectStateToggle value={template!.objectState} onChange={saveObjectState} />
            </div>
          )}
        </div>

        {template && !isNew && (
          <StatsBar
            items={[
              { label: t('common.id'), content: <StatsBarId value={template.id} /> },
              {
                label: t('reportsModule.reportType'),
                content: t(`reportTypes.${template.reportType}.simple`, {
                  defaultValue: template.reportType,
                }),
              },
              {
                label: t('stats.activity'),
                content: (
                  <StatsBarActivity
                    created={formatDate(template.createTime)}
                    updated={formatDate(template.updateTime)}
                  />
                ),
              },
            ]}
          />
        )}

        <Card>
          <CardHeader>
            <CardTitle>
              <CardIcon icon={LayoutTemplate} />
              {t('reportTemplates.details')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="field-grid">
              <div className="p-field full">
                <FieldLabel>{t('common.description')}</FieldLabel>
                <form.Field name="description">
                  {(field) => (
                    <Input
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder={t('reportTemplates.descriptionPlaceholder')}
                    />
                  )}
                </form.Field>
              </div>
              <div className="p-field">
                <FieldLabel
                  hint={
                    values.reportType
                      ? t(`reportTypes.${values.reportType}.explanation`)
                      : undefined
                  }
                >
                  {t('reportsModule.reportType')}
                </FieldLabel>
                <form.Field
                  name="reportType"
                  validators={{
                    onSubmit: ({ value }) =>
                      value ? undefined : t('reportTemplates.reportTypeRequired'),
                  }}
                >
                  {(field) => (
                    <>
                      <Select
                        value={field.state.value}
                        onValueChange={(v) => changeType(v as ReportType)}
                      >
                        <SelectTrigger aria-invalid={field.state.meta.errors.length > 0}>
                          <SelectValue placeholder={t('reportTemplates.selectReportType')}>
                            {field.state.value
                              ? t(`reportTypes.${field.state.value}.simple`)
                              : null}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {reportTypes.map((type) => (
                            <SelectItem key={type} value={type}>
                              {t(`reportTypes.${type}.simple`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {field.state.meta.errors[0] && (
                        <p className="text-xs text-destructive">
                          {field.state.meta.errors[0] as string}
                        </p>
                      )}
                    </>
                  )}
                </form.Field>
              </div>
              <div className="p-field">
                <FieldLabel tooltip={t('reportTemplates.formatHelp')}>
                  {t('reportTemplates.format')}
                </FieldLabel>
                <RadioGroup
                  value={values.reportFormat}
                  onValueChange={(v) => set('reportFormat', v as ReportFormat)}
                  className="flex flex-wrap gap-4 pt-1.5"
                >
                  <Label className="font-normal">
                    <RadioGroupItem value="HTML" />
                    HTML
                  </Label>
                  <Label className="font-normal">
                    <RadioGroupItem value="CSV" />
                    CSV
                  </Label>
                </RadioGroup>
              </div>
              {showTeam && (
                <div className="p-field">
                  <FieldLabel tooltip={t('reportTemplates.teamHelp')}>
                    {t('reportTemplates.team')}
                  </FieldLabel>
                  <SearchSelect
                    value={values.teamId}
                    onChange={(v) => set('teamId', v)}
                    options={teams.map((team) => ({ value: team.id, label: team.name }))}
                    placeholder={t('reportTemplates.general')}
                    fallbackLabel={template?.team?.name}
                    clearable
                    loading={teamsLoading}
                  />
                </div>
              )}
              {politicalNetwork && values.reportType && values.reportType !== 'NETWORK' && (
                <div className="p-field">
                  <FieldLabel hint={t(`reportTemplates.politicalHints.${values.political}`)}>
                    {t('reportTemplates.politicalAds')}
                  </FieldLabel>
                  <Select
                    value={values.political}
                    onValueChange={(v) => set('political', v as PoliticalFilter)}
                  >
                    <SelectTrigger>
                      <SelectValue>{t(`reportTemplates.politicalFilters.${values.political}`)}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {POLITICAL_FILTERS.map((filter) => (
                        <SelectItem key={filter} value={filter}>
                          {t(`reportTemplates.politicalFilters.${filter}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              <CardIcon icon={Layers} />
              {t('reportTemplates.components')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ReportComponentsEditor
              reportType={values.reportType}
              reportFormat={values.reportFormat}
              value={values.components}
              onChange={(components) => set('components', components)}
            />
          </CardContent>
        </Card>

        {isHtml && (
          <Card>
            <CardHeader>
              <CardTitle>
                <CardIcon icon={Code} />
                {t('reportTemplates.appearance')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-4">
                <div className="field-grid">
                  <div className="p-field full">
                    <SwitchField
                      label={t('reportTemplates.customLogo')}
                      description={
                        values.customLogo
                          ? t('reportTemplates.customLogoOn')
                          : t('reportTemplates.customLogoOff')
                      }
                      checked={values.customLogo}
                      onCheckedChange={(checked) => set('customLogo', checked)}
                    />
                    {values.customLogo && (
                      <Input
                        type="url"
                        value={values.logo}
                        onChange={(e) => set('logo', e.target.value)}
                        placeholder="https://"
                      />
                    )}
                  </div>
                  <div className="p-field full">
                    <FieldLabel tooltip={t('reportTemplates.reportTranslationHelp')}>
                      {t('reportTemplates.reportTranslation')}
                    </FieldLabel>
                    <SearchSelect
                      value={values.reportTranslationId}
                      onChange={(v) => set('reportTranslationId', v)}
                      options={translations.map((tr) => ({ value: tr.id, label: tr.name }))}
                      placeholder={t('reportTemplates.defaultEnglish')}
                      fallbackLabel={template?.reportTranslation?.name}
                      clearable
                      loading={translationsLoading}
                    />
                  </div>
                </div>
                <AdvancedSection open={htmlOpen} onOpenChange={setHtmlOpen}>
                  <div className="flex flex-col gap-4">
                    <div className="p-field">
                      <FieldLabel hint={t('reportTemplates.headerHint')}>
                        {t('reportTemplates.header')}
                      </FieldLabel>
                      <CodeEditor
                        value={values.header}
                        onChange={(v) => set('header', v)}
                        variables="none"
                        minHeight="120px"
                      />
                    </div>
                    <div className="p-field">
                      <FieldLabel hint={t('reportTemplates.footerHint')}>
                        {t('reportTemplates.footer')}
                      </FieldLabel>
                      <CodeEditor
                        value={values.footer}
                        onChange={(v) => set('footer', v)}
                        variables="none"
                        minHeight="120px"
                      />
                    </div>
                  </div>
                </AdvancedSection>
              </div>
            </CardContent>
          </Card>
        )}

        {showLabels && (
          <Card>
            <CardHeader>
              <CardTitle>
                <CardIcon icon={Languages} />
                {t('reportTemplates.labels')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-3">
                <p className="text-xs text-muted-foreground">{t('reportTemplates.labelsHelp')}</p>
                <AdvancedSection
                  open={labelsOpen}
                  onOpenChange={setLabelsOpen}
                  activeCount={Object.values(values.labels).filter((v) => v.trim()).length}
                  actions={languages.map((language) => (
                    <Button
                      key={language}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        loadLabels(language)
                        setLabelsOpen(true)
                      }}
                    >
                      <Download className="size-3.5" />
                      {t('reportTemplates.loadLabels', {
                        language: t(`reportTranslations.languages.${language.toUpperCase()}`, {
                          defaultValue: language,
                        }),
                      })}
                    </Button>
                  ))}
                >
                  <div className="grid gap-x-4 gap-y-3 md:grid-cols-2">
                    {Object.keys(englishLabels).map((key) => (
                      <div key={key} className="p-field">
                        <FieldLabel htmlFor={`tpl-label-${key}`}>{englishLabels[key]}</FieldLabel>
                        <Input
                          id={`tpl-label-${key}`}
                          value={values.labels[key] ?? ''}
                          placeholder={englishLabels[key]}
                          onChange={(e) => set(`labels.${key}`, e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                </AdvancedSection>
              </div>
            </CardContent>
          </Card>
        )}

        <div
          className={`detail-savebar${isDirty || isNew || mutation.isPending ? ' is-visible' : ''}`}
        >
          <div className="detail-savebar__left">
            {mutation.isPending && (
              <>
                <Loader2 className="size-4 animate-spin" />
                {isNew ? t('common.creating') : t('common.saving')}
              </>
            )}
          </div>
          <div className="detail-savebar__actions">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate(LIST_PATH)}
              disabled={mutation.isPending}
            >
              <X className="size-4" />
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {isNew ? <Plus className="size-4" /> : <Check className="size-4" />}
              {isNew ? t('reportTemplates.newTemplate') : t('common.save')}
            </Button>
          </div>
        </div>
      </form>
      {template && copyOpen && (
        <CopyReportItemDialog
          open={copyOpen}
          onOpenChange={setCopyOpen}
          kind={t('objectTypes.reportTemplate')}
          source={template}
          copy={(newId, name, description) =>
            ReportTemplatesApi.copy(template, newId, name, description)
          }
          listKey="report-templates"
          pathFor={(newId) => `${LIST_PATH}/${newId}`}
        />
      )}
      <UnsavedChangesDialog blocker={blocker} />
    </AppShell>
  )
}

// ── Outer page ──────────────────────────────────────────────────────────────

export function ReportTemplateEditPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const isNew = searchParams.get('new') === 'true'
  const hasToken = AuthService.isAuthenticated()

  const {
    data: template,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['report-template', id],
    queryFn: () => ReportTemplatesApi.getById(id!),
    enabled: !!id && !isNew && hasToken,
    retry: false,
  })

  const crumbsLoading = [
    { label: t('reportTemplates.title'), href: LIST_PATH },
    { label: isNew ? t('reportTemplates.newTemplate') : t('reportTemplates.editTitle') },
  ]

  if (isError && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <NetworkObjectLookup objectId={id!} />
      </AppShell>
    )
  }

  if (isLoading && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <FormSkeleton rows={4} columns={2} />
      </AppShell>
    )
  }

  return <ReportTemplateEditForm key={id} template={template ?? null} id={id!} isNew={isNew} />
}
